import { Box, useTheme } from '@mui/material';
import { createContext, useContext } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { ReactComponent as WarningTriangleIcon } from '../../../assets/icons/warningTriangle.svg';

type Props = {
    count: number;
};

// AEX-3218: the Advisor Console row supplies the "Review now" click handler through this context,
// because the indicator is rendered via the table's generic cell renderer (no direct prop path).
export const ActionRequiredContext = createContext<{ onReviewNow?: () => void }>({});

// AEX-3570/AEX-3218: "N Goal(s) need attention - Review now" indicator under the client name/customer
// ID on the Advisor Console (flow2/banorte). Clicking it opens the Review Goals modal (step 1).
export const ActionRequiredIndicator = ({ count }: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { onReviewNow } = useContext(ActionRequiredContext);

    if (count <= 0) {
        return null;
    }

    const label = t(
        count === 1 ? 'ADVISOR_CONSOLE_ACTION_REQUIRED_SINGULAR' : 'ADVISOR_CONSOLE_ACTION_REQUIRED_PLURAL',
        { count }
    );

    return (
        <Box
            onClick={
                onReviewNow
                    ? (event) => {
                          // Stop the click reaching the client-name cell button so only this handler runs.
                          event.stopPropagation();
                          onReviewNow();
                      }
                    : undefined
            }
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: theme.palette.semantic.text.default,
                textDecoration: 'none'
            }}
        >
            <Box
                component="span"
                aria-hidden
                sx={{
                    display: 'inline-flex',
                    flexShrink: 0,
                    '& path:first-of-type': { fill: theme.palette.semantic.icon.warning }
                }}
            >
                <WarningTriangleIcon width={12} height={12} />
            </Box>
            <Box
                component="span"
                sx={{
                    fontFamily: '"TT Commons Pro"',
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: '16px',
                    letterSpacing: '0.12px',
                    textDecoration: 'none'
                }}
            >
                {label}
            </Box>
            <ChevronRightIcon
                sx={{ width: 12, height: 12, flexShrink: 0, color: theme.palette.semantic.text.default }}
            />
        </Box>
    );
};

export default ActionRequiredIndicator;
